import { tgpu, d } from 'typegpu';
import type { TgpuRoot, TgpuBindGroup, TgpuBindGroupLayout, TgpuGuardedComputePipeline, TgpuBuffer } from 'typegpu';
import type { StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { VizMode } from '../config/schema';
import { DataCell } from '../systems/shared/DataCell';
import { readTgpuBufferFloats } from './Context';

const REDUCTION_THREADS = 256;

const ReduceParams = d.struct({
  totalCells: d.f32,
  channel: d.f32,
  _pad2: d.f32,
  _pad3: d.f32,
});

const MaxArray = d.arrayOf(d.f32, REDUCTION_THREADS);

function channelForVizMode(vm: VizMode): number {
  switch (vm) {
    case 'divergence': return 0;
    case 'distance':
    case 'divergenceDistance':
    case 'position':
    case 'furthestNeighbor':
    case 'totalNeighborDistance':
    case 'ftle':
    case 'closenessOfReturn': return 2;
  }
}

export class Reductions {
  private root: TgpuRoot;
  private layout: TgpuBindGroupLayout<{
    data: { storage: WgslArray<typeof DataCell> };
    maxBuf: { storage: typeof MaxArray; access: 'mutable' };
    params: { uniform: typeof ReduceParams };
  }>;
  private pipeline!: TgpuGuardedComputePipeline;
  private bindGroup: TgpuBindGroup;
  private maxBuffer: TgpuBuffer<typeof MaxArray> & StorageFlag;
  private paramsBuffer: TgpuBuffer<typeof ReduceParams> & UniformFlag;
  private dataArrType: WgslArray<typeof DataCell>;
  private cellCount: number;
  private vizMode: VizMode;

  constructor(root: TgpuRoot, dataArray: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag, cellCount: number, vizMode: VizMode) {
    this.root = root;
    this.cellCount = cellCount;
    this.vizMode = vizMode;
    this.dataArrType = d.arrayOf(DataCell, cellCount);
    this.maxBuffer = root.createBuffer(MaxArray, new Array(REDUCTION_THREADS).fill(0)).$usage('storage') as unknown as TgpuBuffer<typeof MaxArray> & StorageFlag;
    this.paramsBuffer = root.createBuffer(ReduceParams, { totalCells: cellCount, channel: channelForVizMode(vizMode), _pad2: 0, _pad3: 0 }).$usage('uniform') as unknown as TgpuBuffer<typeof ReduceParams> & UniformFlag;
    this.layout = tgpu.bindGroupLayout({
      data: { storage: this.dataArrType },
      maxBuf: { storage: MaxArray, access: 'mutable' },
      params: { uniform: ReduceParams },
    });
    this.bindGroup = root.createBindGroup(this.layout, { data: dataArray, maxBuf: this.maxBuffer, params: this.paramsBuffer });
    this.buildPipeline();
  }

  updateData(dataArray: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag): void {
    this.bindGroup = this.root.createBindGroup(this.layout, { data: dataArray, maxBuf: this.maxBuffer, params: this.paramsBuffer });
  }

  setMode(vizMode: VizMode): void {
    this.vizMode = vizMode;
    this.paramsBuffer.write({ totalCells: this.cellCount, channel: channelForVizMode(vizMode), _pad2: 0, _pad3: 0 } as never);
  }

  private buildPipeline(): void {
    const layout = this.layout;
    const reduceMax = (threadIndex: number) => {
      'use gpu';
      const total = d.u32(layout.$.params.totalCells);
      const chunk = (total + 255) / 256;
      const channel = layout.$.params.channel;
      let localMax: number = 0;
      const start = d.u32(threadIndex) * d.u32(chunk);
      for (let i = 0; i < chunk; i++) {
        const idx = start + d.u32(i);
        if (idx >= total) break;
        const cell = layout.$.data[idx];
        let val: number = 0;
        if (channel < 0.5) {
          val = cell.r as number;
        } else if (channel < 1.5) {
          val = cell.g as number;
        } else {
          val = cell.b as number;
        }
        if (val > localMax) {
          localMax = val;
        }
      }
      layout.$.maxBuf[threadIndex] = localMax;
    };
    this.pipeline = this.root.createGuardedComputePipeline(reduceMax);
  }

  async computeMax(): Promise<number> {
    if (this.vizMode === 'position') return 1.0;
    this.pipeline.with(this.bindGroup).dispatchThreads(REDUCTION_THREADS);
    const result = await readTgpuBufferFloats(this.root, this.maxBuffer, REDUCTION_THREADS * 4);
    let max = 0;
    for (let i = 0; i < REDUCTION_THREADS; i++) {
      if (result[i] > max) max = result[i];
    }
    return max;
  }
}
